import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { RegimeDependenceProfileReportRecord, TradingStrategy } from '@/types/appRouter';
import { trpc, trpcUnbatched } from '@/api/trpc';
import { showToast } from '@/components/Layout/Toast';
import { buildTradeLogicEditorPath } from '@/features/tradeLogicAuthoring/tradeLogicNavigation';
import { useAppQuery } from '@/hooks/useAppQuery';
import { RegimeVetoCandidateCard } from './RegimeVetoCandidateCard';
import { buildVetoCandidateCardModels, deriveRegimeVetoVariantName, type RegimeVetoCandidateCardModel } from './regimeVetoPresentation';

interface RegimeVetoCandidateSectionProps {
    record: RegimeDependenceProfileReportRecord;
    /** Loaded source strategy; null while loading or when unavailable. */
    strategy: TradingStrategy | null;
    /** Design rule 6: a report profiled under a superseded model must not drive the action. */
    lineageStale: boolean;
}

/**
 * Layer 3 — veto candidate cards plus the "Create gated variant" action path: create an inactive
 * sibling variant (`strategyVariant.createFromStrategy`, mirroring the source's screener/scorecard
 * attachments so the veto is the only difference), then deep-link into the Trade Logic editor's
 * fork-and-return flow with a `seedCondition` param that pre-adds the veto to the copied entry draft.
 */
export function RegimeVetoCandidateSection({ record, strategy, lineageStale }: RegimeVetoCandidateSectionProps) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [pendingCandidateKey, setPendingCandidateKey] = useState<string | null>(null);

    const cardModels = useMemo(
        () => buildVetoCandidateCardModels(record.report.verdict, record.report.thresholds),
        [record.report.thresholds, record.report.verdict]
    );

    const entryVersionId = strategy?.tradeLogicV2EntryVersionId ?? null;

    // Resolves the source entry attachment (versionId → definitionId + version) for the fork deep link.
    const entryVersionQuery = useAppQuery(
        {
            ...trpcUnbatched.strategy.fetchTradeLogicDefinitionVersionById.queryOptions({ versionId: entryVersionId ?? 0 }),
            enabled: cardModels.length > 0 && entryVersionId !== null
        },
        { fallbackErrorMessage: 'Failed to resolve the strategy entry attachment' }
    );

    const createVariantMutation = useMutation(
        trpc.strategyVariant.createFromStrategy.mutationOptions({
            onError: (error) => showToast(error.message, 'error')
        })
    );

    if (cardModels.length === 0) return null;

    const entryVersion = entryVersionQuery.data?.version ?? null;

    const disabledReason = lineageStale
        ? 'Profiled under a superseded model — re-run the profile before acting on this report.'
        : strategy === null
          ? 'Strategy details are still loading.'
          : strategy.id !== record.strategyId
            ? 'The loaded strategy does not match this report.'
            : entryVersionId === null
              ? 'The strategy has no Trade Logic V2 entry attachment to fork.'
              : entryVersionQuery.isLoading
                ? 'Resolving the entry attachment…'
                : entryVersion === null
                  ? 'The entry attachment could not be resolved.'
                  : null;

    const createGatedVariant = async (model: RegimeVetoCandidateCardModel) => {
        // One creation at a time across ALL candidates: two candidates racing would create two variants.
        if (pendingCandidateKey !== null) return;
        if (strategy === null || entryVersion === null) return;

        setPendingCandidateKey(model.key);

        try {
            const result = await createVariantMutation
                .mutateAsync({
                    aero: { action: 'none' },
                    description:
                        `Regime-gated variant of "${strategy.name}": vetoes ${model.candidate.excludedStates.join(', ')}. ` +
                        `Created from regime profile report #${record.id} (experiment ${record.experimentId}).`,
                    name: deriveRegimeVetoVariantName(strategy.name, model.candidate.excludedStates),
                    sourceStrategyId: record.strategyId,
                    variantLabel: model.variantLabel,
                    ...(strategy.activeScorecardId != null ? { scorecardId: strategy.activeScorecardId } : {}),
                    ...(strategy.activeScreenerRuleId != null ? { screener: { action: 'reuseAttached' as const } } : {})
                })
                .catch(() => null);

            if (result === null) return;

            if (!result.success) {
                showToast(result.errorMessages.join(' ') || 'Failed to create the gated variant.', 'error');
                return;
            }

            if (result.scorecardAttachment && !result.scorecardAttachment.attached) {
                showToast(
                    result.scorecardAttachment.warning ??
                        'The gated variant was created, but the scorecard could not be attached. Attach it from the strategy page.',
                    'warning',
                    true
                );
            }

            showToast('Gated variant created — review the seeded veto condition and publish.', 'success');

            void queryClient.invalidateQueries(trpcUnbatched.strategy.fetchStrategies.pathFilter());
            void queryClient.invalidateQueries(trpc.strategyVariant.listFamilies.pathFilter());

            void navigate(
                buildTradeLogicEditorPath('entry', 'new', `/user/tradingStrategy/${result.strategyId}`, {
                    copyFromDefinitionId: entryVersion.definitionId,
                    copyFromVersion: entryVersion.version,
                    seedCondition: model.seedCondition
                })
            );
        } finally {
            setPendingCandidateKey(null);
        }
    };

    return (
        <div className="space-y-4">
            {cardModels.map((model) => (
                <RegimeVetoCandidateCard
                    key={model.key}
                    disabledReason={
                        disabledReason ??
                        (pendingCandidateKey !== null && pendingCandidateKey !== model.key
                            ? 'Another gated variant is being created.'
                            : null)
                    }
                    isCreating={pendingCandidateKey === model.key}
                    model={model}
                    onCreateGatedVariant={() => void createGatedVariant(model)}
                />
            ))}
        </div>
    );
}
